import { useState } from 'react';
import propTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

import { leerLibroFamilia, leerDni, formatoAceptado, FORMATOS_LIBRO } from '../../../api/brechaGenero';
import { FormHeader, FormSection, FileField, FileListField, SubmitButton } from './FormFields';

/**
 * Paso 1: subir la documentación para que los lectores la interpreten.
 *
 * Son dos lecturas independientes que van en paralelo:
 *   - el libro de familia da los hijos y el otro progenitor
 *   - el DNI del solicitante da su nombre, apellidos, número y domicilio
 *
 * Se puede saltar: el formulario se rellena a mano igual de bien.
 */
const SubidaLibroFamilia = ({ onAnalizado, onSaltar, onBack }) => {
    const { t } = useTranslation();
    const [paginas, setPaginas] = useState([]);
    const [dniFrontal, setDniFrontal] = useState(null);
    const [dniReverso, setDniReverso] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const cambiarPaginas = (_, files) => {
        const validas = files.filter(formatoAceptado);
        if (validas.length < files.length) {
            toast.error(t('Hay archivos con un formato que el lector no acepta y se han descartado'));
        }
        setPaginas(validas);
    };

    const analizar = async (event) => {
        event.preventDefault();
        if (paginas.length === 0 && !dniFrontal) {
            toast.error(t('Sube el libro de familia, el DNI, o los dos'));
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // En paralelo: son dos lectores distintos y ninguno depende del otro.
            const [libro, dni] = await Promise.all([
                paginas.length > 0 ? leerLibroFamilia(paginas) : Promise.resolve(null),
                dniFrontal ? leerDni(dniFrontal, dniReverso) : Promise.resolve(null),
            ]);
            onAnalizado({ libro, dni }, { paginas, dniFrontal, dniReverso });
        } catch (e) {
            console.error('Error al leer la documentación:', e);
            setError(e.response?.data?.detail || e.message || t('Error desconocido'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={analizar}>
            <FormHeader
                emoji="📖"
                title={t('Rellenando documento de')}
                highlight={t('Brecha de Género')}
                onBack={onBack}
                backLabel={t('Volver atrás')}
            />

            <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-4">
                <div className="flex gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
                    <div className="text-sm text-amber-900 dark:text-amber-100">
                        <p className="font-semibold mb-1">{t('Sube todas las páginas con datos')}</p>
                        <p>{t('Puedes escanear el libro abierto, de dos en dos: el lector busca todas las fichas de persona que haya en cada imagen. Sáltate las páginas en blanco y la tapa.')}</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>{t('Formatos aceptados')}: {FORMATOS_LIBRO.map((e) => e.replace('.', '').toUpperCase()).join(', ')}. {t('Los PDF no valen.')}</li>
                            <li>{t('Del libro de familia se extraen nombre, apellidos y fecha de nacimiento de cada hijo y del otro progenitor.')}</li>
                            <li>{t('No pasa nada si una página sale repetida en dos escaneos: las fichas duplicadas se descartan solas.')}</li>
                            <li>{t('Del DNI se extraen el número, el nombre, los apellidos y el domicilio del solicitante.')}</li>
                        </ul>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-6 rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 p-4">
                    <div className="flex gap-3">
                        <XCircleIcon className="w-5 h-5 flex-shrink-0 text-red-600 dark:text-red-500 mt-0.5" />
                        <div className="text-sm text-red-900 dark:text-red-100">
                            <p className="font-semibold mb-1">{t('No se ha podido leer la documentación')}</p>
                            <p className="break-words">{error}</p>
                            <p className="mt-2">{t('Puedes volver a intentarlo o rellenar el formulario a mano.')}</p>
                        </div>
                    </div>
                </div>
            )}

            <FormSection title={t('Páginas del libro de familia')}>
                <FileListField
                    name="libro_familia"
                    label={t('Selecciona todas las páginas de una vez')}
                    files={paginas}
                    onChange={cambiarPaginas}
                    accept="image/*"
                    disabled={loading}
                    hint={t('Se guardan para anexarlas al final del PDF, así que no hará falta volver a subirlas.')}
                    vacio={t('Todavía no has seleccionado ninguna página')}
                />
            </FormSection>

            <FormSection
                title={t('DNI del solicitante')}
                description={t('Se lee para rellenar sus datos, y se anexa al PDF junto al libro de familia')}
            >
                <FileField
                    span={1}
                    name="dni_frontal"
                    label={t('DNI / NIE · anverso')}
                    accept="image/*"
                    file={dniFrontal}
                    onChange={(_, file) => setDniFrontal(file)}
                    disabled={loading}
                />
                <FileField
                    span={1}
                    name="dni_reverso"
                    label={t('DNI / NIE · reverso')}
                    accept="image/*"
                    file={dniReverso}
                    onChange={(_, file) => setDniReverso(file)}
                    disabled={loading}
                    hint={t('Sin el reverso no se puede sacar el domicilio')}
                />
            </FormSection>

            <div className="mt-10">
                <SubmitButton
                    loading={loading}
                    label={t('Leer la documentación')}
                    loadingLabel={t('Leyendo las imágenes...')}
                />
            </div>

            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('¿Prefieres no usar el lector?')}{' '}
                <button
                    type="button"
                    onClick={onSaltar}
                    disabled={loading}
                    className="text-primary-600 hover:underline dark:text-primary-500 disabled:opacity-50"
                >
                    {t('Rellenar el formulario a mano')}
                </button>
            </p>
        </form>
    );
};

SubidaLibroFamilia.propTypes = {
    onAnalizado: propTypes.func.isRequired,
    onSaltar: propTypes.func.isRequired,
    onBack: propTypes.func.isRequired,
};

export default SubidaLibroFamilia;
